import React, { useState } from 'react';
import { API_URL } from '../config';

const CustomEAForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    strategy_name: '',
    platform: 'mt5',
    indicators: '',
    logic: '',
    news_filter: false,
    trailing_stop: false,
    martingale: false,
    time_filter: false
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      const res = await fetch(`${API_URL}/api/forms/custom-ea`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', strategy_name: '', platform: 'mt5', indicators: '', logic: '', news_filter: false, trailing_stop: false, martingale: false, time_filter: false });
        setTimeout(() => setStatus({ loading: false, success: false, error: null }), 5000);
      } else {
        setStatus({ loading: false, success: false, error: 'Failed to submit request.' });
      }
    } catch (err) {
      console.error(err);
      setStatus({ loading: false, success: false, error: 'Network error.' });
    }
  };

  return (
    <section className="pb-24 px-margin-mobile md:px-margin-desktop max-w-[900px] mx-auto">
      <div className="glass-card p-8 md:p-12 rounded-xl shadow-2xl relative overflow-hidden">
        {/* Golden edge accent */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary/40 to-transparent"></div>
        
        {status.success && (
          <div className="mb-8 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 font-bold flex items-center gap-3">
            <span className="material-symbols-outlined">check_circle</span>
            Request submitted! We will contact you soon.
          </div>
        )}
        {status.error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 font-bold flex items-center gap-3">
            <span className="material-symbols-outlined">error</span>
            {status.error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block font-label-md text-label-md text-silver-text" htmlFor="name">Your Name</label>
              <input required value={formData.name} onChange={handleChange} className="w-full bg-surface-container-lowest border border-white/10 rounded-lg py-3 px-4 text-on-surface focus:ring-0 focus:border-primary gold-glow transition-all" id="name" name="name" placeholder="John Doe" type="text"/>
            </div>
            <div className="space-y-2">
              <label className="block font-label-md text-label-md text-silver-text" htmlFor="email">Email Address</label>
              <input required value={formData.email} onChange={handleChange} className="w-full bg-surface-container-lowest border border-white/10 rounded-lg py-3 px-4 text-on-surface focus:ring-0 focus:border-primary gold-glow transition-all" id="email" name="email" placeholder="john@example.com" type="email"/>
            </div>
          </div>
          {/* Strategy Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block font-label-md text-label-md text-silver-text" htmlFor="strategy_name">Strategy Name</label>
              <input required value={formData.strategy_name} onChange={handleChange} className="w-full bg-surface-container-lowest border border-white/10 rounded-lg py-3 px-4 text-on-surface focus:ring-0 focus:border-primary gold-glow transition-all" id="strategy_name" name="strategy_name" placeholder="e.g. Trend Follower Pro" type="text"/>
            </div>
            <div className="space-y-2">
              <label className="block font-label-md text-label-md text-silver-text" htmlFor="platform">Trading Platform</label>
              <select required value={formData.platform} onChange={handleChange} className="w-full bg-surface-container-lowest border border-white/10 rounded-lg py-3 px-4 text-on-surface focus:ring-0 focus:border-primary gold-glow transition-all" id="platform" name="platform">
                <option value="mt4">MetaTrader 4 (MT4)</option>
                <option value="mt5">MetaTrader 5 (MT5)</option>
                <option value="both">Both Platforms</option>
              </select>
            </div>
          </div>
          {/* Technical Inputs */}
          <div className="space-y-2">
            <label className="block font-label-md text-label-md text-silver-text" htmlFor="indicators">Indicators Used</label>
            <input required value={formData.indicators} onChange={handleChange} className="w-full bg-surface-container-lowest border border-white/10 rounded-lg py-3 px-4 text-on-surface focus:ring-0 focus:border-primary gold-glow transition-all" id="indicators" name="indicators" placeholder="e.g. RSI (14), EMA 50, Bollinger Bands" type="text"/>
            <p className="text-[11px] text-on-surface-variant italic">List all indicators and their standard parameters.</p>
          </div>
          {/* Logic Description */}
          <div className="space-y-2">
            <label className="block font-label-md text-label-md text-silver-text" htmlFor="logic">Detailed Logic &amp; Description</label>
            <textarea required value={formData.logic} onChange={handleChange} className="w-full bg-surface-container-lowest border border-white/10 rounded-lg py-3 px-4 text-on-surface focus:ring-0 focus:border-primary gold-glow transition-all resize-none" id="logic" name="logic" placeholder="Describe your entry rules, exit rules, risk management, and any specific behaviors you want the EA to have..." rows="6"></textarea>
          </div>
          {/* Features Checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            <div className="flex items-center gap-3 group">
              <input checked={formData.news_filter} onChange={handleChange} className="w-5 h-5 rounded border-white/10 bg-surface-container-lowest text-primary focus:ring-0 checked:bg-primary transition-all" name="news_filter" id="news_filter" type="checkbox"/>
              <label className="text-on-surface-variant group-hover:text-silver-text cursor-pointer transition-colors text-body-md" htmlFor="news_filter">Include News Filter</label>
            </div>
            <div className="flex items-center gap-3 group">
              <input checked={formData.trailing_stop} onChange={handleChange} className="w-5 h-5 rounded border-white/10 bg-surface-container-lowest text-primary focus:ring-0 checked:bg-primary transition-all" name="trailing_stop" id="trailing_stop" type="checkbox"/>
              <label className="text-on-surface-variant group-hover:text-silver-text cursor-pointer transition-colors text-body-md" htmlFor="trailing_stop">Advanced Trailing Stop</label>
            </div>
            <div className="flex items-center gap-3 group">
              <input checked={formData.martingale} onChange={handleChange} className="w-5 h-5 rounded border-white/10 bg-surface-container-lowest text-primary focus:ring-0 checked:bg-primary transition-all" name="martingale" id="martingale" type="checkbox"/>
              <label className="text-on-surface-variant group-hover:text-silver-text cursor-pointer transition-colors text-body-md" htmlFor="martingale">Martingale/Recovery Logic</label>
            </div>
            <div className="flex items-center gap-3 group">
              <input checked={formData.time_filter} onChange={handleChange} className="w-5 h-5 rounded border-white/10 bg-surface-container-lowest text-primary focus:ring-0 checked:bg-primary transition-all" name="time_filter" id="time_filter" type="checkbox"/>
              <label className="text-on-surface-variant group-hover:text-silver-text cursor-pointer transition-colors text-body-md" htmlFor="time_filter">Session/Time Filters</label>
            </div>
          </div>
          <div className="pt-8 flex flex-col md:flex-row items-center gap-6">
            <button disabled={status.loading} className="w-full md:w-auto bg-primary text-on-primary font-bold px-12 py-4 rounded shadow-[0_10px_20px_rgba(240,185,11,0.15)] hover:shadow-[0_15px_30px_rgba(240,185,11,0.25)] transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2" type="submit">
              {status.loading && <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>}
              Submit Request
            </button>
            <p className="text-on-surface-variant text-label-sm text-center md:text-left">
              We usually respond with a quote and timeline within 24 hours.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default CustomEAForm;
